import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import Navbar from '../components/Navbar';
import TeamLogo from '../components/TeamLogo';
import ManualOddsPanel from '../components/admin/ManualOddsPanel';
import AutoPickPanel from '../components/admin/AutoPickPanel';
import { 
  Users, Target, RefreshCw, Plus, Trash2, Check, 
  AlertCircle, Trophy, ChevronRight, Star, UserPlus, Key, ClipboardCheck,
  Lock, RotateCcw
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('tips');
  const [users, setUsers] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [loading, setLoading] = useState(true);

  // Tip entry state
  const [selectedUser, setSelectedUser] = useState('');
  const [selectedSport, setSelectedSport] = useState('AFL');
  const [selectedRound, setSelectedRound] = useState(1);
  const [rounds, setRounds] = useState([]);
  const [roundFixtures, setRoundFixtures] = useState([]);
  const [tipSelections, setTipSelections] = useState({});
  const [marginSelections, setMarginSelections] = useState({});
  const [goalkickerSelections, setGoalkickerSelections] = useState({});
  const [submittingTips, setSubmittingTips] = useState(false);
  const [existingTips, setExistingTips] = useState({});

  // Adjustment state
  const [adjUser, setAdjUser] = useState('');
  const [adjPoints, setAdjPoints] = useState('');
  const [adjReason, setAdjReason] = useState('');
  const [adjSport, setAdjSport] = useState('');

  // Register user state
  const [newUserName, setNewUserName] = useState('');
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserRole, setNewUserRole] = useState('user');
  const [registering, setRegistering] = useState(false);

  // Password reset state
  const [resetUser, setResetUser] = useState(null); // {user_id, name}
  const [resetPassword, setResetPassword] = useState('');
  const [resetting, setResetting] = useState(false);

  // GOTR state
  const [gotrSport, setGotrSport] = useState('AFL');
  const [gotrRound, setGotrRound] = useState(1);
  const [gotrRounds, setGotrRounds] = useState([]);
  const [gotrFixtures, setGotrFixtures] = useState([]);
  const [gotrLoading, setGotrLoading] = useState(false);
  // Goalkicker input drafts keyed by fixture_id (for the GOTR AFL row)
  const [gkDraft, setGkDraft] = useState({});
  const [savingGk, setSavingGk] = useState({});

  // BP Audit state
  const [auditRound, setAuditRound] = useState(1);
  const [auditRounds, setAuditRounds] = useState([]);
  const [auditData, setAuditData] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);

  // WC Knockouts state — admin sets ET-inclusive score + progressing team
  const [knockoutFixtures, setKnockoutFixtures] = useState([]);
  const [knockoutLoading, setKnockoutLoading] = useState(false);
  const [knockoutDrafts, setKnockoutDrafts] = useState({}); // {fixture_id: {home_score, away_score, progressing_team}}
  const [knockoutSaving, setKnockoutSaving] = useState({});

  // Finalisation status for the currently-selected (user, sport, round)
  const [isFinalised, setIsFinalised] = useState(false);
  const [reopening, setReopening] = useState(false);

  useEffect(() => {
    fetchUsers();
    fetchAdjustments();
  }, []);

  useEffect(() => {
    fetchRounds();
  }, [selectedSport]);

  useEffect(() => {
    if (selectedRound !== null) fetchRoundFixtures();
  }, [selectedSport, selectedRound]);

  useEffect(() => {
    if (selectedUser && roundFixtures.length > 0) fetchExistingTips();
  }, [selectedUser, roundFixtures]);

  // Check if user has finalised this (sport, round) — drives the Reopen button.
  useEffect(() => {
    if (!selectedUser || selectedRound == null) {
      setIsFinalised(false);
      return;
    }
    const compId = selectedSport === 'AFL' ? 'comp_afl_public_2026'
      : selectedSport === 'NRL' ? 'comp_nrl_public_2026'
      : 'comp_wc_public_2026';
    let cancelled = false;
    (async () => {
      try {
        // The /tips/finalisation-status endpoint reflects the calling user.
        // For an admin checking another tipster we ask /admin/finalisation-status if available,
        // else fall back to checking via /admin/user-tips. For simplicity here we hit a
        // dedicated lightweight check.
        const res = await axios.get(
          `${API_URL}/api/admin/user-finalisation?user_id=${selectedUser}&sport=${selectedSport}&round_number=${selectedRound}&competition_id=${compId}`,
          { withCredentials: true }
        );
        if (!cancelled) setIsFinalised(!!res.data?.is_finalised);
      } catch (_e) {
        if (!cancelled) setIsFinalised(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedUser, selectedSport, selectedRound]);

  const handleReopenTips = async () => {
    if (!selectedUser || selectedRound == null) return;
    const userName = getUserName(selectedUser);
    if (!window.confirm(`Reopen ${selectedSport} Round ${selectedRound} tips for ${userName}? They'll be able to edit and re-finalise (their existing tips stay in place).`)) {
      return;
    }
    const compId = selectedSport === 'AFL' ? 'comp_afl_public_2026'
      : selectedSport === 'NRL' ? 'comp_nrl_public_2026'
      : 'comp_wc_public_2026';
    setReopening(true);
    try {
      await axios.post(`${API_URL}/api/admin/reopen-tips`, {
        user_id: selectedUser,
        sport: selectedSport,
        round_number: selectedRound,
        competition_id: compId,
      }, { withCredentials: true });
      toast.success(`Reopened ${selectedSport} R${selectedRound} for ${userName}`);
      setIsFinalised(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reopen tips');
    }
    setReopening(false);
  };

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/users`, { withCredentials: true });
      setUsers(res.data);
    } catch { toast.error('Failed to load users'); }
    setLoading(false);
  };

  const fetchAdjustments = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/points-adjustments`, { withCredentials: true });
      setAdjustments(res.data);
    } catch {}
  };

  const fetchRounds = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fixtures/${selectedSport}/rounds?season=2026`, { withCredentials: true });
      setRounds(res.data);
      if (res.data.length > 0 && !res.data.includes(selectedRound)) setSelectedRound(res.data[0]);
    } catch {}
  };

  const fetchRoundFixtures = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/fixtures/${selectedSport}?round_number=${selectedRound}&season=2026`, { withCredentials: true });
      setRoundFixtures(res.data);
      setTipSelections({});
      setMarginSelections({});
      setGoalkickerSelections({});
    } catch {}
  };

  const fetchExistingTips = async () => {
    if (!selectedUser) return;
    try {
      const compId = selectedSport === 'AFL' ? 'comp_afl_public_2026' : 'comp_nrl_public_2026';
      const res = await axios.get(
        `${API_URL}/api/admin/user-tips/${selectedUser}?competition_id=${compId}&round_number=${selectedRound}&sport=${selectedSport}`,
        { withCredentials: true }
      );
      const map = {};
      res.data.forEach(t => {
        map[t.fixture_id] = {
          predicted_winner: t.predicted_winner,
          margin_prediction: t.margin_prediction,
          points_earned: t.points_earned,
          is_correct: t.is_correct
        };
      });
      setExistingTips(map);
      // Pre-fill tip selections with existing tips
      const selections = {};
      const margins = {};
      const goalkickers = {};
      res.data.forEach(t => {
        selections[t.fixture_id] = t.predicted_winner;
        if (t.margin_prediction != null) margins[t.fixture_id] = t.margin_prediction;
        if (t.goalkicker_prediction) goalkickers[t.fixture_id] = t.goalkicker_prediction;
      });
      setTipSelections(prev => ({ ...prev, ...selections }));
      setMarginSelections(prev => ({ ...prev, ...margins }));
      setGoalkickerSelections(prev => ({ ...prev, ...goalkickers }));
    } catch {}
  };

  const handleSelectTip = (fixtureId, team) => {
    setTipSelections(prev => ({ ...prev, [fixtureId]: team }));
  };

  const handleSubmitTips = async () => {
    if (!selectedUser) { toast.error('Select a user'); return; }
    const tips = Object.entries(tipSelections)
      .filter(([, winner]) => winner)
      .map(([fixtureId, winner]) => ({
        fixture_id: fixtureId,
        predicted_winner: winner,
        margin_prediction: marginSelections[fixtureId] || null,
        goalkicker_prediction: goalkickerSelections[fixtureId] || null,
      }));
    
    if (tips.length === 0) { toast.error('No tips selected'); return; }
    
    setSubmittingTips(true);
    try {
      const compId = selectedSport === 'AFL' ? 'comp_afl_public_2026' : 'comp_nrl_public_2026';
      const res = await axios.post(`${API_URL}/api/admin/bulk-tips`, {
        user_id: selectedUser,
        competition_id: compId,
        tips
      }, { withCredentials: true });
      toast.success(`${res.data.submitted} tips saved & scored`);
      fetchExistingTips();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit tips');
    }
    setSubmittingTips(false);
  };

  const handleSyncScores = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/sync-scores`, {}, { withCredentials: true });
      toast.success(`Synced: AFL ${res.data.afl_updated}, NRL ${res.data.nrl_updated} updated. ${res.data.tips_scored} tips scored.`);
    } catch (err) {
      toast.error('Sync failed');
    }
  };

  const handleAddAdjustment = async () => {
    if (!adjUser || !adjPoints) { toast.error('User and points required'); return; }
    try {
      await axios.post(`${API_URL}/api/admin/points-adjustment`, {
        user_id: adjUser,
        points: parseInt(adjPoints),
        reason: adjReason || 'Manual adjustment',
        sport: adjSport || null
      }, { withCredentials: true });
      toast.success('Adjustment added');
      setAdjPoints(''); setAdjReason(''); setAdjUser(''); setAdjSport('');
      fetchAdjustments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleDeleteAdjustment = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/admin/points-adjustment/${id}`, { withCredentials: true });
      toast.success('Adjustment deleted');
      fetchAdjustments();
    } catch { toast.error('Failed to delete'); }
  };

  const getUserName = (uid) => users.find(u => u.user_id === uid)?.name || uid;

  // ============ Register user ============
  const handleRegisterUser = async (e) => {
    e.preventDefault();
    if (!newUserName.trim() || !newUserEmail.trim() || !newUserPassword) {
      toast.error('Name, email and password are required');
      return;
    }
    if (newUserPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setRegistering(true);
    try {
      await axios.post(`${API_URL}/api/admin/register-user`, {
        name: newUserName.trim(),
        email: newUserEmail.trim().toLowerCase(),
        password: newUserPassword,
        role: newUserRole,
      }, { withCredentials: true });
      toast.success(`${newUserName} registered`);
      setNewUserName(''); setNewUserEmail(''); setNewUserPassword(''); setNewUserRole('user');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to register user');
    }
    setRegistering(false);
  };

  const handleDeleteUser = async (uid, name) => {
    if (!window.confirm(`Delete ${name}? This removes all their tips and adjustments.`)) return;
    try {
      await axios.delete(`${API_URL}/api/admin/users/${uid}`, { withCredentials: true });
      toast.success(`${name} deleted`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleResetPassword = (uid, name) => {
    setResetUser({ user_id: uid, name });
    setResetPassword('');
  };

  const submitResetPassword = async (e) => {
    e.preventDefault();
    if (!resetUser) return;
    if (resetPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setResetting(true);
    try {
      await axios.post(
        `${API_URL}/api/admin/users/${resetUser.user_id}/reset-password`,
        { password: resetPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${resetUser.name}`);
      setResetUser(null);
      setResetPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
    setResetting(false);
  };

  // ============ GOTR ============
  useEffect(() => {
    if (activeTab !== 'gotr') return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/fixtures/${gotrSport}/rounds?season=2026`, { withCredentials: true });
        setGotrRounds(res.data);
        if (res.data.length > 0 && !res.data.includes(gotrRound)) setGotrRound(res.data[0]);
      } catch {}
    })();
  }, [gotrSport, activeTab]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'gotr' || gotrRound === null) return;
    (async () => {
      setGotrLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/fixtures/${gotrSport}?round_number=${gotrRound}&season=2026`, { withCredentials: true });
        setGotrFixtures(res.data);
      } catch {
        setGotrFixtures([]);
      }
      setGotrLoading(false);
    })();
  }, [gotrSport, gotrRound, activeTab]);

  const handleSetGotr = async (fixtureId, value = true) => {
    try {
      await axios.post(`${API_URL}/api/admin/fixtures/set-gotr`, { fixture_id: fixtureId, value }, { withCredentials: true });
      toast.success(value ? 'Game of the Round set' : 'GOTR flag cleared');
      // refresh fixtures
      const res = await axios.get(`${API_URL}/api/fixtures/${gotrSport}?round_number=${gotrRound}&season=2026`, { withCredentials: true });
      setGotrFixtures(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update GOTR');
    }
  };

  const handleClearGotr = async () => {
    if (!window.confirm(`Clear GOTR for ${gotrSport} Round ${gotrRound}?`)) return;
    try {
      await axios.post(`${API_URL}/api/admin/fixtures/clear-gotr`, {
        sport: gotrSport, round_number: gotrRound, season: 2026
      }, { withCredentials: true });
      toast.success('GOTR cleared for round');
      const res = await axios.get(`${API_URL}/api/fixtures/${gotrSport}?round_number=${gotrRound}&season=2026`, { withCredentials: true });
      setGotrFixtures(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    }
  };

  const handleSaveGoalkickers = async (fixtureId) => {
    const raw = gkDraft[fixtureId] ?? '';
    // Split by comma OR newline, trim, drop empties
    const list = raw
      .split(/[,\n]/)
      .map(s => s.trim())
      .filter(Boolean);
    setSavingGk(prev => ({ ...prev, [fixtureId]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/fixtures/set-goalkickers`, {
        fixture_id: fixtureId,
        goalkickers: list,
      }, { withCredentials: true });
      toast.success(list.length === 0 ? 'Goalkickers cleared' : `Saved ${list.length} goalkicker${list.length > 1 ? 's' : ''}`);
      // refresh fixtures so actual_goalkickers shows up
      const res = await axios.get(`${API_URL}/api/fixtures/${gotrSport}?round_number=${gotrRound}&season=2026`, { withCredentials: true });
      setGotrFixtures(res.data);
      setGkDraft(prev => ({ ...prev, [fixtureId]: '' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save goalkickers');
    }
    setSavingGk(prev => ({ ...prev, [fixtureId]: false }));
  };

  // ============ BP Audit ============
  useEffect(() => {
    if (activeTab !== 'audit') return;
    (async () => {
      try {
        const res = await axios.get(`${API_URL}/api/fixtures/AFL/rounds?season=2026`, { withCredentials: true });
        setAuditRounds(res.data);
        if (res.data.length > 0 && !res.data.includes(auditRound)) setAuditRound(res.data[0]);
      } catch (_e) { /* ignore */ }
    })();
  }, [activeTab]);  // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab !== 'audit' || auditRound === null) return;
    (async () => {
      setAuditLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/goalkicker-audit/${auditRound}`, { withCredentials: true });
        setAuditData(res.data);
      } catch (err) {
        setAuditData(null);
        toast.error(err.response?.data?.detail || 'Failed to load audit');
      }
      setAuditLoading(false);
    })();
  }, [auditRound, activeTab]);

  // Load WC knockout fixtures when the Knockouts tab is opened
  useEffect(() => {
    if (activeTab !== 'knockouts') return;
    (async () => {
      setKnockoutLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/admin/wc-knockout-fixtures`, { withCredentials: true });
        const fxs = res.data.fixtures || [];
        setKnockoutFixtures(fxs);
        // Seed drafts with existing values
        const drafts = {};
        for (const f of fxs) {
          drafts[f.fixture_id] = {
            home_score: f.home_score ?? '',
            away_score: f.away_score ?? '',
            progressing_team: f.progressing_team || '',
          };
        }
        setKnockoutDrafts(drafts);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Failed to load knockout fixtures');
      }
      setKnockoutLoading(false);
    })();
  }, [activeTab]);

  const handleSaveKnockout = async (fixture) => {
    const draft = knockoutDrafts[fixture.fixture_id] || {};
    const hs = draft.home_score === '' || draft.home_score == null ? null : parseInt(draft.home_score, 10);
    const as_ = draft.away_score === '' || draft.away_score == null ? null : parseInt(draft.away_score, 10);
    if (hs == null || as_ == null || Number.isNaN(hs) || Number.isNaN(as_)) {
      toast.error('Enter both home and away scores (extra-time-inclusive, excluding penalty shootout)');
      return;
    }
    setKnockoutSaving(prev => ({ ...prev, [fixture.fixture_id]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/fixture-result`, {
        fixture_id: fixture.fixture_id,
        home_score: hs,
        away_score: as_,
        is_completed: true,
        progressing_team: draft.progressing_team || null,
      }, { withCredentials: true });
      toast.success(`Saved ${fixture.home_team} ${hs}–${as_} ${fixture.away_team}`);
      // Refresh
      const res = await axios.get(`${API_URL}/api/admin/wc-knockout-fixtures`, { withCredentials: true });
      setKnockoutFixtures(res.data.fixtures || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save knockout result');
    }
    setKnockoutSaving(prev => ({ ...prev, [fixture.fixture_id]: false }));
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-black uppercase tracking-tighter text-[#1A1A1A]" data-testid="admin-heading">Admin Panel</h1>
            <p className="text-gray-500 mt-1">Manage tips, scores, and point adjustments</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                if (!window.confirm('Pull real EPL 2026/27 schedule from TheSportsDB and reconcile fixtures? This may take ~1 minute.')) return;
                try {
                  toast.loading('Importing EPL schedule…', { id: 'epl-sched' });
                  const res = await axios.post(`${API_URL}/api/admin/epl-schedule/import`, {}, { withCredentials: true });
                  const s = res.data;
                  toast.success(
                    `EPL schedule reconciled — ${s.match_time_updated} kickoffs / ${s.round_updated} rounds / ${s.score_updated} scores updated`,
                    { id: 'epl-sched', duration: 6000 }
                  );
                } catch (err) {
                  toast.error(err.response?.data?.detail || 'EPL schedule import failed', { id: 'epl-sched' });
                }
              }}
              variant="outline"
              className="border-[#37003C] text-[#37003C] hover:bg-[#37003C] hover:text-white"
              data-testid="epl-schedule-import-btn"
            >
              <RefreshCw className="w-4 h-4 mr-2" /> Import EPL Schedule
            </Button>
            <Button onClick={handleSyncScores} className="bg-[#007AFF] hover:bg-[#3395FF]" data-testid="sync-scores-btn">
              <RefreshCw className="w-4 h-4 mr-2" /> Sync & Score
            </Button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-sm p-1 mb-8">
          {[
            { id: 'tips', label: 'Enter Tips', icon: Target },
            { id: 'stale', label: 'Stale Fixtures', icon: AlertCircle },
            { id: 'odds', label: 'Odds & Away Tips', icon: Target },
            { id: 'adjustments', label: 'Points Adjustments', icon: Trophy },
            { id: 'gotr', label: 'Game of the Round', icon: Star },
            { id: 'audit', label: 'BP Audit', icon: ClipboardCheck },
            { id: 'knockouts', label: 'WC Knockouts', icon: Trophy },
            { id: 'users', label: 'Register User', icon: UserPlus },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 text-sm font-semibold uppercase tracking-wider rounded-sm transition-all ${
                activeTab === tab.id ? 'bg-white text-[#007AFF] shadow-sm' : 'text-gray-500 hover:text-gray-700'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>

        {/* Enter Tips Tab */}
        {activeTab === 'tips' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="tipster-card">
              <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">User</Label>
                  <Select value={selectedUser} onValueChange={setSelectedUser}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="admin-user-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {users.filter(u => u.role !== 'admin').map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Sport</Label>
                  <Select value={selectedSport} onValueChange={setSelectedSport}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="admin-sport-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="AFL">AFL</SelectItem>
                      <SelectItem value="NRL">NRL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Round</Label>
                  <Select value={selectedRound?.toString()} onValueChange={v => setSelectedRound(parseInt(v))}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="admin-round-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-60">
                      {rounds.map(r => (
                        <SelectItem key={r} value={r.toString()}>{r === 0 ? 'Opening Round' : `Round ${r}`}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    onClick={handleSubmitTips}
                    disabled={submittingTips || !selectedUser || Object.keys(tipSelections).length === 0}
                    className="w-full bg-[#007AFF] hover:bg-[#3395FF]"
                    data-testid="admin-submit-tips-btn"
                  >
                    {submittingTips ? 'Saving...' : 'Save All Tips'}
                  </Button>
                </div>
              </div>
              {selectedUser && (
                <div className="mt-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="text-sm text-gray-500">
                    Entering tips for <span className="font-semibold text-[#1A1A1A]">{getUserName(selectedUser)}</span>
                    {' '} — {Object.values(tipSelections).filter(Boolean).length}/{roundFixtures.length} tipped
                    {isFinalised && (
                      <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-sm bg-[#FFCC00]/20 text-[#8B6914] border border-[#FFCC00]/40">
                        <Lock className="w-3 h-3" /> Finalised
                      </span>
                    )}
                  </div>
                  {isFinalised && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleReopenTips}
                      disabled={reopening}
                      className="border-[#FFCC00] text-[#8B6914] hover:bg-yellow-50 font-semibold"
                      data-testid="admin-reopen-tips-btn"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                      {reopening ? 'Reopening…' : `Reopen ${selectedSport} R${selectedRound}`}
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Fixtures Grid */}
            {!selectedUser ? (
              <div className="tipster-card text-center py-10">
                <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">Select a user to enter their tips</p>
              </div>
            ) : (
              <div className="space-y-2">
                {roundFixtures.map((fixture) => {
                  const selected = tipSelections[fixture.fixture_id];
                  const existing = existingTips[fixture.fixture_id];
                  const isCompleted = fixture.is_completed;
                  const winner = isCompleted && fixture.home_score != null ?
                    (fixture.home_score > fixture.away_score ? fixture.home_team : fixture.away_team) : null;

                  return (
                    <div key={fixture.fixture_id} className="tipster-card py-3 px-4" data-testid={`admin-fixture-${fixture.fixture_id}`}>
                      <div className="flex items-center gap-3">
                        {/* Home team button */}
                        <button
                          onClick={() => handleSelectTip(fixture.fixture_id, fixture.home_team)}
                          className={`flex items-center gap-2 flex-1 p-2 rounded-sm transition-all border-2 cursor-pointer ${
                            selected === fixture.home_team
                              ? 'border-[#007AFF] bg-blue-50'
                              : 'border-gray-200 hover:border-[#007AFF]/50 hover:bg-blue-50/40'
                          }`}
                          data-testid={`admin-tip-home-${fixture.fixture_id}`}
                        >
                          <TeamLogo teamName={fixture.home_team} size={32} />
                          <div className="text-left min-w-0">
                            <div className="font-semibold text-sm truncate">{fixture.home_team}</div>
                            {isCompleted && <span className="text-xs font-bold text-gray-600">{fixture.home_score}</span>}
                          </div>
                        </button>

                        {/* Centre info */}
                        <div className="flex flex-col items-center flex-shrink-0 w-16">
                          {isCompleted ? (
                            <span className="text-xs font-bold text-gray-400">FT</span>
                          ) : (
                            <span className="text-xs text-gray-400">vs</span>
                          )}
                          {existing && (
                            <span className={`text-xs font-bold mt-0.5 ${
                              existing.is_correct === true ? 'text-[#34C759]' : 
                              existing.is_correct === false ? 'text-[#FF3B30]' : 'text-[#007AFF]'
                            }`}>
                              {existing.is_correct === true ? `+${existing.points_earned}` : 
                               existing.is_correct === false ? '0' : 'Tipped'}
                            </span>
                          )}
                        </div>

                        {/* Away team button */}
                        <button
                          onClick={() => handleSelectTip(fixture.fixture_id, fixture.away_team)}
                          className={`flex items-center gap-2 flex-1 p-2 rounded-sm transition-all border-2 flex-row-reverse cursor-pointer ${
                            selected === fixture.away_team
                              ? 'border-[#007AFF] bg-blue-50'
                              : 'border-gray-200 hover:border-[#007AFF]/50 hover:bg-blue-50/40'
                          }`}
                          data-testid={`admin-tip-away-${fixture.fixture_id}`}
                        >
                          <TeamLogo teamName={fixture.away_team} size={32} />
                          <div className="text-right min-w-0">
                            <div className="font-semibold text-sm truncate">{fixture.away_team}</div>
                            {isCompleted && <span className="text-xs font-bold text-gray-600">{fixture.away_score}</span>}
                          </div>
                        </button>

                        {/* GOTR badge */}
                        {fixture.is_game_of_round && (
                          <span className="tipster-badge text-xs flex-shrink-0">GOTR</span>
                        )}
                      </div>

                      {/* GOTR bonus inputs */}
                      {fixture.is_game_of_round && selected && (
                        <div className="mt-3 pt-3 border-t border-gray-100 flex flex-wrap gap-4">
                          <div>
                            <label className="text-xs text-gray-500 block mb-1">Margin</label>
                            <Input
                              type="number"
                              min="0"
                              max="200"
                              value={marginSelections[fixture.fixture_id] ?? ''}
                              onChange={e => setMarginSelections(prev => ({ ...prev, [fixture.fixture_id]: parseInt(e.target.value) || null }))}
                              className="w-24 h-8 text-sm bg-white border-gray-200"
                              placeholder="Pts"
                              data-testid={`admin-margin-${fixture.fixture_id}`}
                            />
                          </div>
                          {selectedSport === 'AFL' && (
                            <div>
                              <label className="text-xs text-gray-500 block mb-1">Leading Goalkicker</label>
                              <Input
                                type="text"
                                value={goalkickerSelections[fixture.fixture_id] ?? ''}
                                onChange={e => setGoalkickerSelections(prev => ({ ...prev, [fixture.fixture_id]: e.target.value || null }))}
                                className="w-44 h-8 text-sm bg-white border-gray-200"
                                placeholder="Player name"
                                data-testid={`admin-goalkicker-${fixture.fixture_id}`}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Stale Fixtures Tab */}
        {activeTab === 'stale' && <StaleFixturesPanel />}
        {activeTab === 'odds' && <div className="space-y-6"><AutoPickPanel /><ManualOddsPanel /></div>}

        {/* Points Adjustments Tab */}
        {activeTab === 'adjustments' && (
          <div className="space-y-6">
            {/* Add Adjustment */}
            <div className="tipster-card">
              <h3 className="text-lg font-bold uppercase tracking-tight mb-4">Add Adjustment</h3>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">User</Label>
                  <Select value={adjUser} onValueChange={setAdjUser}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="adj-user-select">
                      <SelectValue placeholder="Select user" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      {users.map(u => (
                        <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Points (+/-)</Label>
                  <Input
                    type="number"
                    value={adjPoints}
                    onChange={e => setAdjPoints(e.target.value)}
                    className="bg-white border-gray-200"
                    placeholder="e.g. 3 or -1"
                    data-testid="adj-points-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Sport (optional)</Label>
                  <Select value={adjSport} onValueChange={setAdjSport}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="adj-sport-select">
                      <SelectValue placeholder="General" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="AFL">AFL</SelectItem>
                      <SelectItem value="NRL">NRL</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Reason</Label>
                  <Input
                    value={adjReason}
                    onChange={e => setAdjReason(e.target.value)}
                    className="bg-white border-gray-200"
                    placeholder="e.g. GOTR bonus"
                    data-testid="adj-reason-input"
                  />
                </div>
                <div className="flex items-end">
                  <Button onClick={handleAddAdjustment} className="w-full bg-[#007AFF] hover:bg-[#3395FF]" data-testid="adj-submit-btn">
                    <Plus className="w-4 h-4 mr-2" /> Add
                  </Button>
                </div>
              </div>
            </div>

            {/* Adjustments List */}
            <div className="tipster-card p-0">
              <div className="grid grid-cols-[1fr,5rem,5rem,1fr,3rem] gap-2 px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100">
                <div>User</div>
                <div className="text-center">Points</div>
                <div className="text-center">Sport</div>
                <div>Reason</div>
                <div></div>
              </div>
              {adjustments.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No adjustments yet</div>
              ) : (
                adjustments.map(adj => (
                  <div key={adj.adjustment_id} className="grid grid-cols-[1fr,5rem,5rem,1fr,3rem] gap-2 items-center px-4 py-3 border-b border-gray-50 hover:bg-gray-50">
                    <div className="font-semibold text-sm">{getUserName(adj.user_id)}</div>
                    <div className={`text-center font-bold text-sm ${adj.points >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'}`}>
                      {adj.points >= 0 ? '+' : ''}{adj.points}
                    </div>
                    <div className="text-center text-xs text-gray-500">{adj.sport || 'General'}</div>
                    <div className="text-sm text-gray-600 truncate">{adj.reason}</div>
                    <button onClick={() => handleDeleteAdjustment(adj.adjustment_id)} className="text-gray-400 hover:text-[#FF3B30]">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Game of the Round Tab */}
        {activeTab === 'gotr' && (
          <div className="space-y-6" data-testid="gotr-panel">
            <div className="tipster-card">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Sport</Label>
                  <Select value={gotrSport} onValueChange={setGotrSport}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="gotr-sport-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="AFL">AFL</SelectItem>
                      <SelectItem value="NRL">NRL</SelectItem>
                      <SelectItem value="WC">World Cup</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Round</Label>
                  <Select value={String(gotrRound)} onValueChange={v => setGotrRound(parseInt(v))}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="gotr-round-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-72">
                      {gotrRounds.map(r => (
                        <SelectItem key={r} value={String(r)}>
                          {r === 0 ? 'Opening Round' : `Round ${r}`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  onClick={handleClearGotr}
                  variant="outline"
                  className="border-gray-200 hover:border-[#FF3B30] hover:text-[#FF3B30]"
                  data-testid="gotr-clear-btn"
                >
                  Clear GOTR for round
                </Button>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Click a fixture to toggle it as Game of the Round. You can flag <strong>multiple</strong> fixtures per round (all sports). Click "GOTR" again to remove the flag.
              </p>
            </div>

            {/* Fixtures grid */}
            <div className="space-y-2" data-testid="gotr-fixtures-list">
              {gotrLoading ? (
                <div className="text-center py-8 text-gray-400">Loading…</div>
              ) : gotrFixtures.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No fixtures in this round</div>
              ) : (
                gotrFixtures.map(fx => {
                  const isGotr = fx.is_game_of_round;
                  const showGkEditor = isGotr && gotrSport === 'AFL';
                  const currentGks = fx.actual_goalkickers || [];
                  return (
                    <div
                      key={fx.fixture_id}
                      className={`tipster-card py-3 px-4 transition-all ${isGotr ? 'border-[#FFCC00] bg-yellow-50/40' : ''}`}
                      data-testid={`gotr-fixture-${fx.fixture_id}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          <TeamLogo teamName={fx.home_team} size={28} />
                          <span className="font-semibold text-sm truncate">{fx.home_team}</span>
                        </div>
                        <span className="text-xs text-gray-400 font-mono">vs</span>
                        <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                          <span className="font-semibold text-sm truncate text-right">{fx.away_team}</span>
                          <TeamLogo teamName={fx.away_team} size={28} />
                        </div>
                        <div className="w-36 flex-shrink-0 flex justify-end">
                          {isGotr ? (
                            <Button
                              size="sm"
                              onClick={() => handleSetGotr(fx.fixture_id, false)}
                              className="bg-yellow-100 hover:bg-yellow-200 text-[#B8860B] font-bold text-xs"
                              data-testid={`gotr-unset-${fx.fixture_id}`}
                            >
                              <Star className="w-3.5 h-3.5 fill-current mr-1.5" />
                              GOTR · Remove
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-gray-200 hover:border-[#FFCC00] hover:text-[#B8860B] hover:bg-yellow-50 text-xs"
                              onClick={() => handleSetGotr(fx.fixture_id, true)}
                              data-testid={`gotr-set-${fx.fixture_id}`}
                            >
                              Set as GOTR
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Leading Goalkicker editor (AFL GOTR only) */}
                      {showGkEditor && (
                        <div className="mt-3 pt-3 border-t border-yellow-200/60" data-testid={`gk-editor-${fx.fixture_id}`}>
                          <div className="flex items-start gap-3">
                            <div className="flex-1">
                              <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">
                                Leading Goalkicker(s)
                              </Label>
                              {currentGks.length > 0 ? (
                                <div className="flex flex-wrap gap-1.5 mb-2">
                                  {currentGks.map((g, idx) => (
                                    <span
                                      key={idx}
                                      className="inline-flex items-center gap-1.5 bg-yellow-100 text-[#8B6914] text-xs font-semibold px-2 py-1 rounded-sm"
                                      data-testid={`gk-tag-${fx.fixture_id}-${idx}`}
                                    >
                                      <Star className="w-3 h-3 fill-current" />
                                      {g}
                                    </span>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-400 mb-2">No leading goalkicker recorded yet</p>
                              )}
                              <Input
                                value={gkDraft[fx.fixture_id] ?? ''}
                                onChange={(e) => setGkDraft(prev => ({ ...prev, [fx.fixture_id]: e.target.value }))}
                                placeholder="e.g. Jeremy Cameron (use commas only when multiple players tied)"
                                className="bg-white border-gray-200 text-sm"
                                data-testid={`gk-input-${fx.fixture_id}`}
                              />
                              <p className="text-[11px] text-gray-500 mt-1">
                                Enter the leading goalkicker's full name. Tipsters who tipped the full name, just the first name, or just the surname all get +1 BP. Use commas ONLY when two different players are tied for top. Saving recalculates all tip scores.
                              </p>
                            </div>
                            <div className="flex flex-col gap-2 pt-6 flex-shrink-0">
                              <Button
                                size="sm"
                                onClick={() => handleSaveGoalkickers(fx.fixture_id)}
                                className="bg-[#FFCC00] hover:bg-[#E6B800] text-[#1A1A1A] font-semibold"
                                disabled={savingGk[fx.fixture_id]}
                                data-testid={`gk-save-${fx.fixture_id}`}
                              >
                                {savingGk[fx.fixture_id] ? 'Saving…' : currentGks.length > 0 ? 'Replace' : 'Save'}
                              </Button>
                              {currentGks.length > 0 && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={async () => {
                                    setGkDraft(prev => ({ ...prev, [fx.fixture_id]: '' }));
                                    await handleSaveGoalkickers(fx.fixture_id);
                                  }}
                                  className="border-gray-200 text-xs hover:text-[#FF3B30] hover:border-[#FF3B30]"
                                  data-testid={`gk-clear-${fx.fixture_id}`}
                                >
                                  Clear
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* BP Audit Tab */}
        {activeTab === 'audit' && (
          <div className="space-y-6" data-testid="audit-panel">
            <div className="tipster-card">
              <div className="flex flex-col sm:flex-row sm:items-end gap-4">
                <div className="flex-1">
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">AFL Round</Label>
                  <Select value={String(auditRound)} onValueChange={v => setAuditRound(parseInt(v))}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="audit-round-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200 max-h-80 overflow-y-auto">
                      {auditRounds.map(r => (
                        <SelectItem key={r} value={String(r)}>Round {r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-gray-500 flex-1 leading-relaxed">
                  Shows every tipster&apos;s goalkicker pick alongside the admin-saved actuals for each AFL GOTR fixture. Helps catch admin typos (e.g. accidentally splitting &quot;Jeremy Cameron&quot; into two chips) before they cause leaderboard disputes.
                </p>
              </div>
            </div>

            {auditLoading ? (
              <div className="text-center py-10 text-gray-400 text-sm">Loading…</div>
            ) : !auditData || auditData.fixtures.length === 0 ? (
              <div className="tipster-card text-center py-10 text-gray-400 text-sm" data-testid="audit-empty">
                No AFL Game-of-the-Round fixtures in Round {auditRound}.
              </div>
            ) : (
              auditData.fixtures.map(fx => (
                <div key={fx.fixture_id} className="tipster-card" data-testid={`audit-fixture-${fx.fixture_id}`}>
                  <div className="flex items-start justify-between mb-3 gap-4 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <TeamLogo teamName={fx.home_team} size={28} />
                      <span className="font-semibold text-sm truncate">{fx.home_team}</span>
                      <span className="text-xs text-gray-400 font-mono px-1">vs</span>
                      <span className="font-semibold text-sm truncate">{fx.away_team}</span>
                      <TeamLogo teamName={fx.away_team} size={28} />
                    </div>
                    <div className="text-xs text-gray-500">
                      {fx.summary.would_award_bp} / {fx.summary.with_prediction} predictions would earn BP
                    </div>
                  </div>

                  <div className="mb-3">
                    <Label className="text-xs uppercase tracking-wider text-gray-500 mb-1.5 block">
                      Admin-saved Leading Goalkicker(s)
                    </Label>
                    {fx.actual_goalkickers.length > 0 ? (
                      <div className="flex flex-wrap gap-1.5">
                        {fx.actual_goalkickers.map((g, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center gap-1.5 bg-yellow-100 text-[#8B6914] text-xs font-semibold px-2 py-1 rounded-sm"
                          >
                            <Star className="w-3 h-3 fill-current" />
                            {g}
                          </span>
                        ))}
                        {fx.actual_goalkickers.length === 2 && (
                          <span className="text-[11px] text-amber-700 ml-2 self-center">
                            Heads up: 2 chips. If this should be one player (e.g. &quot;Jeremy Cameron&quot;), go to Game of the Round → Clear → re-enter the full name.
                          </span>
                        )}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400">Not entered yet.</p>
                    )}
                  </div>

                  <div className="border-t border-gray-100 -mx-4 px-4 pt-3">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wider text-gray-500">
                          <th className="py-1 font-semibold">Tipster</th>
                          <th className="py-1 font-semibold">Their Pick</th>
                          <th className="py-1 font-semibold text-center">BP?</th>
                          <th className="py-1 font-semibold text-right">Current Pts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {fx.rows.map(r => (
                          <tr key={r.user_id} className="border-t border-gray-50" data-testid={`audit-row-${r.user_id}`}>
                            <td className="py-1.5 font-medium">{r.user_name}</td>
                            <td className="py-1.5">
                              {r.prediction ? (
                                <span className={r.matches ? 'text-[#8B6914] font-semibold' : 'text-gray-700'}>
                                  {r.prediction}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">— (no pick{r.is_auto_pick ? ', auto' : ''})</span>
                              )}
                            </td>
                            <td className="py-1.5 text-center">
                              {!r.prediction ? (
                                <span className="text-gray-300">—</span>
                              ) : r.matches ? (
                                <span className="inline-flex items-center gap-1 text-green-700 font-semibold">
                                  <Check className="w-3.5 h-3.5" />+1
                                </span>
                              ) : (
                                <span className="text-gray-400">✗</span>
                              )}
                            </td>
                            <td className="py-1.5 text-right font-mono">{r.current_points}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* WC Knockouts Tab — set ET-inclusive score + progressing team */}
        {activeTab === 'knockouts' && (
          <div className="space-y-4" data-testid="knockouts-panel">
            <div className="tipster-card">
              <div className="flex items-start justify-between gap-4 flex-wrap mb-1">
                <div>
                  <h3 className="text-lg font-bold uppercase tracking-tight">WC Knockout Results</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Enter the <span className="font-semibold">extra-time-inclusive</span> aggregate score
                    (do NOT add penalty-shootout goals). When a match is decided on penalties or extra
                    time, set the <span className="font-semibold">Progressing Team</span> so that team
                    is counted as the winner for tipping.
                  </p>
                </div>
                <Button
                  onClick={async () => {
                    setKnockoutLoading(true);
                    try {
                      const res = await axios.post(`${API_URL}/api/admin/wc-knockouts/refresh`, {}, { withCredentials: true });
                      const s = res.data.knockout_refresh || {};
                      toast.success(
                        `Refreshed knockouts: +${s.inserted || 0} new, ${s.updated || 0} updated, ${s.deduped || 0} duplicates removed`,
                      );
                      const r2 = await axios.get(`${API_URL}/api/admin/wc-knockout-fixtures`, { withCredentials: true });
                      const fxs = r2.data.fixtures || [];
                      setKnockoutFixtures(fxs);
                      const drafts = {};
                      for (const f of fxs) {
                        drafts[f.fixture_id] = {
                          home_score: f.home_score ?? '',
                          away_score: f.away_score ?? '',
                          progressing_team: f.progressing_team || '',
                        };
                      }
                      setKnockoutDrafts(drafts);
                    } catch (err) {
                      toast.error(err.response?.data?.detail || 'Failed to refresh knockouts');
                    }
                    setKnockoutLoading(false);
                  }}
                  disabled={knockoutLoading}
                  className="bg-[#007AFF] hover:bg-[#3395FF] flex-shrink-0"
                  data-testid="knockouts-refresh-btn"
                >
                  {knockoutLoading ? 'Refreshing…' : 'Refresh from TheSportsDB'}
                </Button>
              </div>
              {knockoutLoading ? (
                <p className="text-sm text-gray-400">Loading knockout fixtures…</p>
              ) : knockoutFixtures.length === 0 ? (
                <p className="text-sm text-gray-400">No WC knockout fixtures found.</p>
              ) : (
                <div className="space-y-3">
                  {knockoutFixtures.map((fx) => {
                    const draft = knockoutDrafts[fx.fixture_id] || {};
                    const saving = knockoutSaving[fx.fixture_id];
                    return (
                      <div
                        key={fx.fixture_id}
                        className="border border-gray-200 rounded-sm p-3"
                        data-testid={`knockout-fixture-${fx.fixture_id}`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                            R{fx.round_number} · {new Date(fx.match_time).toLocaleString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
                          </div>
                          {fx.is_completed && (
                            <span className="text-[10px] font-bold uppercase text-[#34C759]">Completed</span>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,1fr] gap-3 items-end mb-3">
                          <div className="flex items-center gap-2 min-w-0">
                            <TeamLogo teamName={fx.home_team} size={28} />
                            <span className="font-semibold text-sm truncate">{fx.home_team}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Input
                              type="number"
                              min="0"
                              max="20"
                              value={draft.home_score ?? ''}
                              onChange={(e) => setKnockoutDrafts(prev => ({
                                ...prev,
                                [fx.fixture_id]: { ...prev[fx.fixture_id], home_score: e.target.value },
                              }))}
                              className="w-16 h-9 text-center tabular-nums bg-white border-gray-200"
                              placeholder="0"
                              data-testid={`knockout-home-score-${fx.fixture_id}`}
                            />
                            <span className="text-gray-400">–</span>
                            <Input
                              type="number"
                              min="0"
                              max="20"
                              value={draft.away_score ?? ''}
                              onChange={(e) => setKnockoutDrafts(prev => ({
                                ...prev,
                                [fx.fixture_id]: { ...prev[fx.fixture_id], away_score: e.target.value },
                              }))}
                              className="w-16 h-9 text-center tabular-nums bg-white border-gray-200"
                              placeholder="0"
                              data-testid={`knockout-away-score-${fx.fixture_id}`}
                            />
                          </div>
                          <div className="flex items-center gap-2 min-w-0 justify-end">
                            <span className="font-semibold text-sm truncate text-right">{fx.away_team}</span>
                            <TeamLogo teamName={fx.away_team} size={28} />
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-3 items-end">
                          <div>
                            <Label className="text-xs uppercase tracking-wider text-gray-500 mb-1 block">
                              Progressing Team
                            </Label>
                            <Select
                              value={draft.progressing_team || '__none__'}
                              onValueChange={(v) => setKnockoutDrafts(prev => ({
                                ...prev,
                                [fx.fixture_id]: { ...prev[fx.fixture_id], progressing_team: v === '__none__' ? '' : v },
                              }))}
                            >
                              <SelectTrigger className="w-56 bg-white border-gray-200" data-testid={`knockout-progression-${fx.fixture_id}`}>
                                <SelectValue placeholder="(auto from score)" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">(auto from score)</SelectItem>
                                <SelectItem value={fx.home_team}>{fx.home_team}</SelectItem>
                                <SelectItem value={fx.away_team}>{fx.away_team}</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <Button
                            onClick={() => handleSaveKnockout(fx)}
                            disabled={saving}
                            className="bg-[#007AFF] hover:bg-[#3395FF]"
                            data-testid={`knockout-save-${fx.fixture_id}`}
                          >
                            {saving ? 'Saving…' : 'Save & Re-score'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Register User Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6" data-testid="users-panel">
            <form onSubmit={handleRegisterUser} className="tipster-card">
              <h3 className="text-lg font-bold uppercase tracking-tight mb-4">Register New User</h3>
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Name</Label>
                  <Input
                    value={newUserName}
                    onChange={e => setNewUserName(e.target.value)}
                    className="bg-white border-gray-200"
                    placeholder="Full name"
                    data-testid="reg-name-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Email</Label>
                  <Input
                    type="email"
                    value={newUserEmail}
                    onChange={e => setNewUserEmail(e.target.value)}
                    className="bg-white border-gray-200"
                    placeholder="user@email.com"
                    data-testid="reg-email-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Password</Label>
                  <Input
                    type="text"
                    value={newUserPassword}
                    onChange={e => setNewUserPassword(e.target.value)}
                    className="bg-white border-gray-200"
                    placeholder="min 6 chars"
                    data-testid="reg-password-input"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">Role</Label>
                  <Select value={newUserRole} onValueChange={setNewUserRole}>
                    <SelectTrigger className="bg-white border-gray-200" data-testid="reg-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">
                      <SelectItem value="user">User</SelectItem>
                      <SelectItem value="admin">Admin</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button
                    type="submit"
                    className="w-full bg-[#007AFF] hover:bg-[#3395FF]"
                    disabled={registering}
                    data-testid="reg-submit-btn"
                  >
                    <UserPlus className="w-4 h-4 mr-2" /> {registering ? 'Adding…' : 'Add User'}
                  </Button>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Newly registered users auto-join all public competitions. Share the email + password with them so they can sign in.
              </p>
            </form>

            {/* Users list */}
            <div className="tipster-card p-0">
              <div className="grid grid-cols-[1fr,1fr,6rem,6rem,3rem,3rem] gap-2 px-4 py-3 text-xs uppercase tracking-wider text-gray-400 font-semibold border-b border-gray-100">
                <div>Name</div>
                <div>Email</div>
                <div className="text-center">Role</div>
                <div className="text-center">Joined</div>
                <div></div>
                <div></div>
              </div>
              {users.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">No users yet</div>
              ) : (
                users.map(u => (
                  <div key={u.user_id} className="grid grid-cols-[1fr,1fr,6rem,6rem,3rem,3rem] gap-2 items-center px-4 py-3 border-b border-gray-50 hover:bg-gray-50" data-testid={`user-row-${u.user_id}`}>
                    <div className="font-semibold text-sm">{u.name}</div>
                    <div className="text-sm text-gray-600 truncate">{u.email}</div>
                    <div className="text-center text-xs font-semibold uppercase tracking-wider">
                      <span className={u.role === 'admin' ? 'text-[#007AFF]' : 'text-gray-500'}>{u.role}</span>
                    </div>
                    <div className="text-center text-xs text-gray-500">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' }) : '—'}
                    </div>
                    <button
                      onClick={() => handleResetPassword(u.user_id, u.name)}
                      className="text-gray-400 hover:text-[#007AFF] flex justify-center"
                      data-testid={`reset-pwd-${u.user_id}`}
                      title="Reset password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(u.user_id, u.name)}
                      className="text-gray-400 hover:text-[#FF3B30] flex justify-center"
                      data-testid={`delete-user-${u.user_id}`}
                      title="Delete user"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Password Reset Dialog */}
        <Dialog open={!!resetUser} onOpenChange={(open) => !open && setResetUser(null)}>
          <DialogContent className="bg-white border-gray-200" data-testid="reset-password-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold uppercase tracking-tight">
                Reset Password
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={submitResetPassword} className="space-y-5 mt-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">User</Label>
                <div className="text-sm font-semibold text-[#1A1A1A] bg-gray-50 px-3 py-2.5 rounded-sm">
                  {resetUser?.name}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-gray-500 mb-2 block">New Password</Label>
                <Input
                  type="text"
                  value={resetPassword}
                  onChange={(e) => setResetPassword(e.target.value)}
                  className="bg-white border-gray-200 focus:border-[#007AFF]"
                  placeholder="Min 6 characters"
                  autoFocus
                  data-testid="reset-pwd-input"
                />
                <p className="text-xs text-gray-500 mt-2">
                  The user's current sessions will be invalidated. They'll need to log in again with the new password.
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  className="border-gray-200"
                  onClick={() => setResetUser(null)}
                  data-testid="reset-pwd-cancel"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="bg-[#007AFF] hover:bg-[#3395FF]"
                  disabled={resetting}
                  data-testid="reset-pwd-submit"
                >
                  {resetting ? 'Resetting…' : 'Reset Password'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}

function StaleFixturesPanel() {
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/stale-fixtures`, { withCredentials: true });
      setFixtures(res.data.fixtures || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load stale fixtures');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async (fx) => {
    const d = drafts[fx.fixture_id] || {};
    const hs = d.home_score === '' || d.home_score == null ? null : parseInt(d.home_score, 10);
    const as_ = d.away_score === '' || d.away_score == null ? null : parseInt(d.away_score, 10);
    if (hs == null || as_ == null || Number.isNaN(hs) || Number.isNaN(as_)) {
      toast.error('Enter both home and away scores');
      return;
    }
    setSaving(prev => ({ ...prev, [fx.fixture_id]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/fixture-result`, {
        fixture_id: fx.fixture_id,
        home_score: hs,
        away_score: as_,
        is_completed: true,
      }, { withCredentials: true });
      toast.success(`Saved ${fx.home_team} ${hs}–${as_} ${fx.away_team}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save result');
    }
    setSaving(prev => ({ ...prev, [fx.fixture_id]: false }));
  };

  return (
    <div className="space-y-4" data-testid="admin-stale-fixtures">
      <div className="tipster-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold uppercase tracking-tight">Unresulted Fixtures</h3>
            <p className="text-sm text-gray-500 mt-1">
              Fixtures {'>'}2 hours past kick-off without a final score. Set the result manually when the external sync is running late.
            </p>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="admin-stale-refresh">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-sm text-gray-500 py-6 text-center">Loading…</div>
        ) : fixtures.length === 0 ? (
          <div className="text-sm text-gray-500 py-6 text-center flex items-center justify-center gap-2">
            <Check className="w-4 h-4 text-green-600" />All caught up — no stale fixtures right now.
          </div>
        ) : (
          <div className="space-y-2">
            {fixtures.map(fx => {
              const draft = drafts[fx.fixture_id] || {};
              return (
                <div key={fx.fixture_id} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 border border-amber-200 bg-amber-50/40 rounded-md" data-testid={`stale-row-${fx.fixture_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold uppercase tracking-wider text-amber-700">
                      {fx.sport} · Round {fx.round_number} · {fx.hours_stale}h stale
                    </div>
                    <div className="text-sm font-semibold text-[#1A1A1A] mt-0.5 truncate">
                      {fx.home_team} <span className="text-gray-400">vs</span> {fx.away_team}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      min="0"
                      placeholder="H"
                      className="w-16 text-center"
                      value={draft.home_score ?? ''}
                      onChange={e => setDrafts(p => ({ ...p, [fx.fixture_id]: { ...p[fx.fixture_id], home_score: e.target.value } }))}
                      data-testid={`stale-home-score-${fx.fixture_id}`}
                    />
                    <span className="text-gray-400">–</span>
                    <Input
                      type="number"
                      min="0"
                      placeholder="A"
                      className="w-16 text-center"
                      value={draft.away_score ?? ''}
                      onChange={e => setDrafts(p => ({ ...p, [fx.fixture_id]: { ...p[fx.fixture_id], away_score: e.target.value } }))}
                      data-testid={`stale-away-score-${fx.fixture_id}`}
                    />
                    <Button size="sm" onClick={() => save(fx)} disabled={saving[fx.fixture_id]} data-testid={`stale-save-${fx.fixture_id}`}>
                      {saving[fx.fixture_id] ? 'Saving…' : 'Save'}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ManualOverridePanel onSaved={load} />
    </div>
  );
}

function ManualOverridePanel({ onSaved }) {
  const [sport, setSport] = useState('EPL');
  const [q, setQ] = useState('');
  const [round, setRound] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [saving, setSaving] = useState({});

  const search = async () => {
    setSearching(true);
    try {
      const params = { sport };
      if (q.trim()) params.q = q.trim();
      if (round.toString().trim()) params.round_number = parseInt(round, 10);
      const res = await axios.get(`${API_URL}/api/admin/fixture-search`, { params, withCredentials: true });
      setResults(res.data.fixtures || []);
      if ((res.data.fixtures || []).length === 0) toast.info('No fixtures match those filters');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Search failed');
    }
    setSearching(false);
  };

  const save = async (fx) => {
    const d = drafts[fx.fixture_id] || {};
    const hs = d.home_score === '' || d.home_score == null ? null : parseInt(d.home_score, 10);
    const as_ = d.away_score === '' || d.away_score == null ? null : parseInt(d.away_score, 10);
    if (hs == null || as_ == null || Number.isNaN(hs) || Number.isNaN(as_)) {
      toast.error('Enter both home and away scores');
      return;
    }
    setSaving(p => ({ ...p, [fx.fixture_id]: true }));
    try {
      await axios.post(`${API_URL}/api/admin/fixture-result`, {
        fixture_id: fx.fixture_id,
        home_score: hs,
        away_score: as_,
        is_completed: true,
      }, { withCredentials: true });
      toast.success(`Saved ${fx.home_team} ${hs}–${as_} ${fx.away_team}`);
      // Refresh both this list and the parent stale list so points recompute is visible.
      search();
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save result');
    }
    setSaving(p => ({ ...p, [fx.fixture_id]: false }));
  };

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' }); }
    catch { return iso; }
  };

  return (
    <div className="tipster-card" data-testid="admin-manual-override">
      <div className="mb-4">
        <h3 className="text-lg font-bold uppercase tracking-tight">Manual Override</h3>
        <p className="text-sm text-gray-500 mt-1">
          Enter or correct a result for any fixture — even one already marked completed.
          Points are recalculated automatically after save.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <select
          value={sport}
          onChange={e => setSport(e.target.value)}
          className="border rounded-md px-3 py-2 text-sm"
          data-testid="override-sport"
        >
          <option value="EPL">EPL</option>
          <option value="AFL">AFL</option>
          <option value="NRL">NRL</option>
          <option value="WC">WC</option>
        </select>
        <Input
          placeholder="Team name (e.g. Brighton)"
          value={q}
          onChange={e => setQ(e.target.value)}
          className="flex-1"
          data-testid="override-team"
        />
        <Input
          type="number"
          placeholder="Round #"
          value={round}
          onChange={e => setRound(e.target.value)}
          className="w-full sm:w-28"
          data-testid="override-round"
        />
        <Button onClick={search} disabled={searching} data-testid="override-search-btn">
          {searching ? 'Searching…' : 'Search'}
        </Button>
      </div>

      {results.length > 0 && (
        <div className="space-y-2">
          {results.map(fx => {
            const draft = drafts[fx.fixture_id] || {};
            const currentScore = (fx.home_score != null && fx.away_score != null)
              ? `${fx.home_score}–${fx.away_score}` : '—';
            return (
              <div key={fx.fixture_id} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 border border-gray-200 rounded-md bg-gray-50" data-testid={`override-row-${fx.fixture_id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-bold uppercase tracking-wider text-gray-600">
                    {fx.sport} · Round {fx.round_number} · {fmtDate(fx.match_time)}
                    <span className={`ml-2 px-1.5 py-0.5 rounded ${fx.is_completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      {fx.is_completed ? `Resulted ${currentScore}` : 'Unresulted'}
                    </span>
                  </div>
                  <div className="text-sm font-semibold text-[#1A1A1A] mt-0.5 truncate">
                    {fx.home_team} <span className="text-gray-400">vs</span> {fx.away_team}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="number" min="0" placeholder="H"
                    className="w-16 text-center"
                    value={draft.home_score ?? ''}
                    onChange={e => setDrafts(p => ({ ...p, [fx.fixture_id]: { ...p[fx.fixture_id], home_score: e.target.value } }))}
                    data-testid={`override-home-score-${fx.fixture_id}`}
                  />
                  <span className="text-gray-400">–</span>
                  <Input
                    type="number" min="0" placeholder="A"
                    className="w-16 text-center"
                    value={draft.away_score ?? ''}
                    onChange={e => setDrafts(p => ({ ...p, [fx.fixture_id]: { ...p[fx.fixture_id], away_score: e.target.value } }))}
                    data-testid={`override-away-score-${fx.fixture_id}`}
                  />
                  <Button size="sm" onClick={() => save(fx)} disabled={saving[fx.fixture_id]} data-testid={`override-save-${fx.fixture_id}`}>
                    {saving[fx.fixture_id] ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
